import React, { useEffect, useState, useRef } from "react";
import Layout from "../components/layout";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import MemberCard from "../components/MemberCard";
import "../styles/card.css";

const FamilyCard = () => {
  const [familyMembers, setFamilyMembers] = useState([]);
  const [filteredMembers, setFilteredMembers] = useState([]);
  const [selectedMembers, setSelectedMembers] = useState([]);
  const navigate = useNavigate();
  const printRefs = useRef([]);

  const [filter, setFilter] = useState({
    matricule: "",
    societe: "",
    status: "",
  });

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);

  useEffect(() => {
    const fetchMember = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/santymed/member-list-test`,
          {
            headers: {
              Authorization: "Bearer " + localStorage.getItem("token"),
              "Content-Type": "application/json",
            },
            method: "POST",
          }
        );

        const parseRes = await response.json();
        if (parseRes.success === true) {
          setFamilyMembers(parseRes.Result);
          setFilteredMembers(parseRes.Result);
        } else if (parseRes.message === "NOT AUTHORIZED") {
          localStorage.clear();
          navigate("/login");
        } else {
          console.log("Member not recognized");
        }
      } catch (error) {
        console.error("Error:", error);
      }
    };
    fetchMember();
  }, [navigate]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilter({ ...filter, [name]: value });
  };

  useEffect(() => {
    const applyFilters = () => {
      const { matricule, societe, status } = filter;
      const filtered = familyMembers.filter((item) => {
        return (
          (matricule === "" ||
            item.matricule.toLowerCase().includes(matricule.toLowerCase())) &&
          (societe === "" ||
            item.societe.toLowerCase().includes(societe.toLowerCase()))
        );
      });
      setFilteredMembers(filtered);
      setCurrentPage(1); // Reset to first page after filtering
    };

    applyFilters();
  }, [filter, familyMembers]);

  const handleCardClick = (member) => {
    setSelectedMembers((prevSelected) =>
      prevSelected.includes(member)
        ? prevSelected.filter((m) => m !== member)
        : [...prevSelected, member]
    );
  };

  const handleSelectAll = () => {
    setSelectedMembers([...filteredMembers]);
  };

  const handleUnselectAll = () => {
    setSelectedMembers([]);
  };

  async function renderCardToImage(ref, cardWidthPX, cardHeightPX) {
    const scaleFactor = 300 / 96;
    const clone = ref.cloneNode(true);
    document.body.appendChild(clone);
    Object.assign(clone.style, {
      width: `${cardWidthPX}px`,
      height: `${cardHeightPX}px`,
      position: "absolute",
      left: "-9999px",
      overflow: "hidden",
      borderRadius: "6px",
      backgroundColor: "white",
      padding: "0",
    });
    clone.classList.add("print-view");

    const canvas = await html2canvas(clone, {
      scale: scaleFactor,
      useCORS: true,
      allowTaint: true,
      backgroundColor: "#ffffff",
      width: cardWidthPX,
      height: cardHeightPX,
    });
    document.body.removeChild(clone);

    return canvas.toDataURL("image/png", 1.0);
  }

  // Main function
  const handleDownloadPDF = async () => {
    const cardWidthMM = 85.6;
    const cardHeightMM = 54.0;
    const cardWidthPX = Math.round((cardWidthMM / 25.4) * 300);
    const cardHeightPX = Math.round((cardHeightMM / 25.4) * 300);

    const selectedMembersWithIndices = filteredMembers
      .map((member, index) => ({ member, index }))
      .filter((item) => selectedMembers.includes(item.member));

    if (selectedMembersWithIndices.length === 0) return;

    if (selectedMembersWithIndices.length === 1) {
      const { member, index } = selectedMembersWithIndices[0];
      const input = printRefs.current[index];

      if (input) {
        const imgData = await renderCardToImage(
          input,
          cardWidthPX,
          cardHeightPX
        );

        const pdf = new jsPDF({
          orientation: "landscape",
          unit: "mm",
          format: [cardWidthMM, cardHeightMM],
        });

        pdf.addImage(imgData, "PNG", 0, 0, cardWidthMM, cardHeightMM);
        pdf.save(`${member.prenom}_${member.nom}_card.pdf`);

        try {
          await axios.post(
            `${process.env.REACT_APP_API_URL}/auth/update-print-count`,
            { memberId: member.member_id },
            {
              headers: {
                Authorization: "Bearer " + localStorage.getItem("token"),
                "Content-Type": "application/json",
              },
            }
          );
        } catch (error) {
          console.error("Error updating print count:", error);
        }
      }
    } else {
      const pageWidthMM = 297;
      const pageHeightMM = 210;
      const marginXMM = 10;
      const marginYMM = 10;
      const spacingXMM = 5;
      const spacingYMM = 5;

      const cardsPerRow = Math.floor(
        (pageWidthMM - 2 * marginXMM) / (cardWidthMM + spacingXMM)
      );
      const cardsPerColumn = Math.floor(
        (pageHeightMM - 2 * marginYMM) / (cardHeightMM + spacingYMM)
      );
      const cardsPerPage = cardsPerRow * cardsPerColumn;

      const pdf = new jsPDF({
        orientation: "landscape",
        unit: "mm",
        format: "a4",
      });

      const cardImages = [];
      for (const { member, index } of selectedMembersWithIndices) {
        const input = printRefs.current[index];
        if (input) {
          const imgData = await renderCardToImage(
            input,
            cardWidthPX,
            cardHeightPX
          );
          cardImages.push({ imgData, memberId: member.member_id });
        }
      }

      const totalPages = Math.ceil(cardImages.length / cardsPerPage);

      for (let pageIndex = 0; pageIndex < totalPages; pageIndex++) {
        if (pageIndex > 0) pdf.addPage("a4", "landscape");

        for (let cardIndex = 0; cardIndex < cardsPerPage; cardIndex++) {
          const globalCardIndex = pageIndex * cardsPerPage + cardIndex;
          if (globalCardIndex >= cardImages.length) break;

          const rowIndex = Math.floor(cardIndex / cardsPerRow);
          const colIndex = cardIndex % cardsPerRow;

          const xMM = marginXMM + colIndex * (cardWidthMM + spacingXMM);
          const yMM = marginYMM + rowIndex * (cardHeightMM + spacingYMM);

          pdf.addImage(
            cardImages[globalCardIndex].imgData,
            "PNG",
            xMM,
            yMM,
            cardWidthMM,
            cardHeightMM
          );
        }
      }

      pdf.save(`combined_cards_${new Date().toISOString().slice(0, 10)}.pdf`);

      for (const card of cardImages) {
        try {
          await axios.post(
            `${process.env.REACT_APP_API_URL}/auth/update-print-count`,
            { memberId: card.memberId },
            {
              headers: {
                Authorization: "Bearer " + localStorage.getItem("token"),
                "Content-Type": "application/json",
              },
            }
          );
        } catch (error) {
          console.error("Error updating print count:", error);
        }
      }
    }
  };

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  let currentItems = filteredMembers.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredMembers.length / itemsPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const getPaginationButtons = () => {
    const pageNumbers = [];
    const maxPageButtons = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxPageButtons / 2));
    let endPage = Math.min(totalPages, startPage + maxPageButtons - 1);

    if (endPage - startPage + 1 < maxPageButtons) {
      startPage = Math.max(1, endPage - maxPageButtons + 1);
    }

    if (startPage > 1) {
      pageNumbers.push(
        <li key={1} className="page-item">
          <button onClick={() => paginate(1)} className="page-link">
            1
          </button>
        </li>
      );
      if (startPage > 2) {
        pageNumbers.push(
          <li key="ellipsis-start" className="page-item disabled">
            <span className="page-link">...</span>
          </li>
        );
      }
    }

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(
        <li
          key={i}
          className={`page-item ${i === currentPage ? "active" : ""}`}
        >
          <button onClick={() => paginate(i)} className="page-link">
            {i}
          </button>
        </li>
      );
    }

    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        pageNumbers.push(
          <li key="ellipsis-end" className="page-item disabled">
            <span className="page-link">...</span>
          </li>
        );
      }
      pageNumbers.push(
        <li key={totalPages} className="page-item">
          <button onClick={() => paginate(totalPages)} className="page-link">
            {totalPages}
          </button>
        </li>
      );
    }

    return pageNumbers;
  };

  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  return (
    <Layout>
      <div className="px-5 mt-3">
        <div className="d-flex mb-3 justify-content-center">
          <h3 className="page-title">Cartes Patients</h3>
        </div>

        <div className="col-12 col-sm-6 col-md-4 category">
          Nombre de cartes : {filteredMembers.length}
        </div>

        <div className="d-flex mb-3 filter-bar-card">
          <div className="row ">
            <div className="col-md-3">
              <input
                type="text"
                name="matricule"
                placeholder="Matricule"
                value={filter.matricule}
                onChange={handleFilterChange}
                className="form-control"
              />
            </div>

            <div className="col-md-3">
              <input
                type="text"
                name="societe"
                placeholder="Société"
                value={filter.societe}
                onChange={handleFilterChange}
                className="form-control"
              />
            </div>
          </div>
        </div>
        <div className="d-flex justify-content-around mb-3">
          <button onClick={handleSelectAll} className="btn btn-primary">
            Select All
          </button>
          <button onClick={handleUnselectAll} className="btn btn-primary">
            Unselect All
          </button>
        </div>
        <div className="cards-container">
          {currentItems?.length > 0 ? (
            currentItems.map((member, index) => (
              <div
                className={`container member-card-wrapper ${
                  selectedMembers.includes(member) ? "selected" : ""
                }`}
                key={member.matricule}
                onClick={() => handleCardClick(member)}
                ref={(el) => (printRefs.current[index] = el)}
              >
                <MemberCard memberData={member} isPrintView={false} />
              </div>
            ))
          ) : (
            <p>Loading ...</p>
          )}
        </div>
      </div>
      <div className="d-flex justify-content-center mt-3">
        <button onClick={handleDownloadPDF} className="btn btn-primary">
          {selectedMembers.length > 1
            ? `Download Combined PDF (${selectedMembers.length} cards)`
            : "Download PDF"}
        </button>
      </div>
      <div className="navs d-flex justify-content-between">
        <nav>
          <ul className="pagination">
            <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
              <button
                onClick={() => paginate(currentPage - 1)}
                className="page-link"
              >
                Previous
              </button>
            </li>
            {getPaginationButtons()}
            <li
              className={`page-item ${
                currentPage === totalPages ? "disabled" : ""
              }`}
            >
              <button
                onClick={() => paginate(currentPage + 1)}
                className="page-link"
              >
                Next
              </button>
            </li>
          </ul>
        </nav>
        <div className="items-per-page-select">
          <label htmlFor="itemsPerPage">Items per page:</label>
          <select
            id="itemsPerPage"
            value={itemsPerPage}
            onChange={handleItemsPerPageChange}
            style={{ width: "auto", display: "inline-block" }}
          >
            <option value={5}>5</option>
            <option value={10}>10</option>
            <option value={15}>15</option>
            <option value={50}>50</option>
          </select>
        </div>
      </div>
    </Layout>
  );
};

export default FamilyCard;
